import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowDown, ArrowLeft, ArrowUp, FileText, Loader2, Plus, Save, Sparkles, Trash2, UploadCloud, XCircle,
} from 'lucide-react'
import { useDirty } from '@/hooks/useDirty'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { useToast } from '@/hooks/useToast'
import { useCreateBill, useCreateVendor, useVendors } from '@/features/ap/queries'
import { apApi } from '@/features/ap/api'
import type { BillPayload, DuplicateBillHit, VendorBankAccount, VendorPayload } from '@/features/ap/types'
import { ApiError } from '@/lib/api'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import { countryForCurrency, formatDateTime, normalizeCountryCode } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import { CountryCombobox } from '@/components/CountryCombobox'
import { CurrencyCombobox } from '@/components/CurrencyCombobox'
import { StateProvinceField } from '@/components/StateProvinceField'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'

/**
 * Bill editor with two creation modes:
 *
 *   - "Enter manually" - type everything by hand.
 *   - "Upload PDF + auto-fill" - upload, extract with AI, pre-fill.
 *
 * Mirrors the AR invoice editor's primitives throughout so a change in
 * one place (CurrencyCombobox, Combobox for account codes, reorder
 * arrows) cascades across both AR and AP.
 *
 * There is ONE attachment slot at the top. The mode toggle just
 * controls whether AI extraction fires after the upload:
 *   - manual mode: upload attaches the PDF for record-keeping only.
 *   - upload mode: upload kicks off the async AI extractor and the
 *     form fills in as soon as the poll returns 'ready'.
 *
 * When the extracted vendor doesn't match an existing one, the
 * confirm modal also pre-fills a bank-account draft from extracted
 * bank details. Accepting the modal creates the vendor + a primary
 * bank_account (via apApi.upsertBankAccount) in one user action.
 */

type Mode = 'manual' | 'upload'

type LineDraft = {
  description: string
  account_code: string
  quantity: number | string
  unit_price: number | string
  tax_rate: number | string
}

function blankLine(): LineDraft {
  return { description: '', account_code: '', quantity: 1, unit_price: 0, tax_rate: 0 }
}

function swap<T>(arr: T[], a: number, b: number): T[] {
  if (a < 0 || b < 0 || a >= arr.length || b >= arr.length) return arr
  const next = [...arr]
  ;[next[a], next[b]] = [next[b], next[a]]
  return next
}

// Poll interval for extraction status. Small enough that the UI feels
// live, large enough to avoid hammering the backend with dozens of
// in-flight users.
const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 180_000

// Bank-account draft pre-filled from AI extraction. `label` + `is_primary`
// are the only required fields (matches the VendorDetailPage pattern).
type BankDraft = Partial<VendorBankAccount> & { label: string; is_primary: boolean; account_number?: string | null }

function blankBankDraft(): BankDraft {
  return { label: 'Primary', is_primary: true }
}

export function BillEditorPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const vendorsQ = useVendors({ isActive: true })
  const { data: coaData } = useCoa()
  const createMut = useCreateBill()
  const createVendorMut = useCreateVendor()
  const regional = useRegionalDefaults()

  const initialVendorId = useMemo(() => {
    const v = params.get('vendor_id')
    return v ? parseInt(v, 10) : 0
  }, [params])

  const [mode, setMode] = useState<Mode>('manual')

  const [vendorId, setVendorId] = useState<number>(initialVendorId)
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState('')
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  )
  const [currency, setCurrency] = useState(() => regional.currency)
  // Snap currency + vendorDraft defaults once tenant settings finish loading.
  const regionalSynced = useRef(false)
  useEffect(() => {
    if (!regional.isLoaded || regionalSynced.current) return
    setCurrency(regional.currency)
    setVendorDraft((prev) => ({
      ...prev,
      country: prev.country || regional.country,
      default_currency: prev.default_currency || regional.currency,
    }))
    regionalSynced.current = true
    // We only want this to run once per page mount, after tenant data lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regional.isLoaded])
  const [notes, setNotes] = useState('')
  const [terms, setTerms] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])
  // Header-level totals (Phase 080 / bill-extractor parity with invoices).
  // discount / shipping are kept as raw strings so the input behaves like a
  // normal text field; coerced to numbers on save. headerTaxRatePct is "" =
  // per-line tax mode, "20" = single 20% rate applied to subtotal.
  const [discountInput, setDiscountInput] = useState('')
  const [shippingInput, setShippingInput] = useState('')
  const [headerTaxRatePctInput, setHeaderTaxRatePctInput] = useState('')
  const [useHeaderTax, setUseHeaderTax] = useState(false)
  const [shippingAccountCode, setShippingAccountCode] = useState<string>('')

  // Attachment state - one slot, shared by both modes.
  const [attachment, setAttachment] = useState<{ key: string; filename: string; size: number } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Extraction results - populated when AI mode succeeds.
  const [fieldConfidence, setFieldConfidence] = useState<Record<string, number>>({})
  const [overallConfidence, setOverallConfidence] = useState<number | null>(null)

  // Vendor confirmation popup - opens after extraction when the
  // suggested vendor name doesn't match any existing vendor.
  const [vendorConfirmOpen, setVendorConfirmOpen] = useState(false)
  const [vendorDraft, setVendorDraft] = useState<VendorPayload>(() => ({
    display_name: '', country: regional.country, default_currency: regional.currency, default_payment_terms_days: 30,
  }))
  const [bankDraft, setBankDraft] = useState<BankDraft>(blankBankDraft())

  // Duplicate-confirmation popup - opens when the backend rejects a
  // create with a 409 DUPLICATE_BILL payload. Keeps the pending payload
  // around so "Create anyway" just re-posts with { force: true }.
  const [duplicateHits, setDuplicateHits] = useState<DuplicateBillHit[] | null>(null)
  const [pendingPayload, setPendingPayload] = useState<BillPayload | null>(null)
  const [forceSaving, setForceSaving] = useState(false)

  // Expense account options (Income Statement accounts from the
  // tenant's CoA, filtered to Expenses + COGS - mirror of the AR
  // revenueAccountOptions filter but for the AP side).
  const expenseAccountOptions = useMemo(() => {
    const list = Array.isArray(coaData) ? coaData : []
    return list
      .filter(row => row.account_code && row.statement_type === 'Income Statement'
        && (row.main_category === 'Expenses' || row.main_category === 'Cost of Goods Sold'))
      .map(row => ({
        value: row.account_code,
        label: `${row.account_code} · ${row.subcategory_name}`,
        description: row.main_category,
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [coaData])

  function setLine(i: number, patch: Partial<LineDraft>) {
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l))
  }
  function moveLine(idx: number, direction: -1 | 1) {
    setLines(prev => swap(prev, idx, idx + direction))
  }
  function removeLine(idx: number) {
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx))
  }

  // Busy flag - while uploading or extracting, the user can't change
  // the mode toggle or touch any form field. Only "Remove" works (see
  // removeAttachment below) so the user can bail out.
  const busy = uploading || extracting

  // Removal token: incremented every time the user clicks Remove so
  // any in-flight upload/extraction can detect the cancel and bail
  // instead of racing to fill the (now reset) form. Without this the
  // pending extraction would land AFTER Remove and re-fill every
  // field the user just cleared.
  const removalTokenRef = useRef(0)

  // Reset the editor to a pristine state. Triggered when the user
  // removes an attachment that had already filled the form via AI
  // extraction - every previously-auto-filled field goes back to its
  // default so there's no risk of saving stale data.
  function resetFormToDefaults() {
    setVendorId(initialVendorId || 0)
    setVendorInvoiceNumber('')
    setIssueDate(new Date().toISOString().slice(0, 10))
    setDueDate(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10))
    setCurrency(regional.currency)
    setNotes('')
    setTerms('')
    setLines([blankLine()])
    setFieldConfidence({})
    setOverallConfidence(null)
    setVendorDraft({ display_name: '', country: regional.country, default_currency: regional.currency, default_payment_terms_days: 30 })
    setBankDraft(blankBankDraft())
    setVendorConfirmOpen(false)
  }

  function removeAttachment() {
    // Invalidate any in-flight upload/extraction so they can't race
    // back in and re-populate the form after we've cleared it.
    removalTokenRef.current += 1
    setUploading(false)
    setExtracting(false)
    setAttachment(null)
    resetFormToDefaults()
  }

  // -------------------------------------------------------------------
  // PDF upload. In upload mode, also kicks off AI extraction.
  // -------------------------------------------------------------------
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0]
    if (!f) return
    const token = removalTokenRef.current
    setUploading(true)
    try {
      const res = await apApi.uploadBillAttachment(f)
      // If Remove was clicked while the upload was in flight, drop
      // the result on the floor - don't re-populate the attachment.
      if (token !== removalTokenRef.current) return
      setAttachment({ key: res.key, filename: res.filename, size: res.size })
      toast({ title: `Attached ${res.filename}` })
      if (mode === 'upload') {
        await runExtraction(res.key, token)
      }
    } catch (err) {
      if (token !== removalTokenRef.current) return
      toast({ title: err instanceof Error ? err.message : 'Upload failed', variant: 'destructive' })
    } finally {
      // Only flip the busy flag back if WE still own the attempt -
      // otherwise Remove's own state update stays authoritative.
      if (token === removalTokenRef.current) setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // -------------------------------------------------------------------
  // AI extraction - start + poll. The server returns a 202 with the
  // extraction_id immediately and processes in a background promise;
  // we poll /extract-status/:id until ready or failed.
  // -------------------------------------------------------------------
  async function runExtraction(key: string, inheritedToken?: number) {
    // If the caller didn't hand us a token (e.g. Re-extract button),
    // snapshot the current one so this call still respects Remove.
    const token = inheritedToken ?? removalTokenRef.current
    setExtracting(true)
    try {
      const start = await apApi.startBillExtraction(key)
      if (token !== removalTokenRef.current) return
      const extractionId = start.extraction_id
      const deadline = Date.now() + POLL_TIMEOUT_MS

      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error('Extraction timed out - retry or enter manually.')
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS))
        // Abort the poll loop when Remove was clicked mid-flight.
        if (token !== removalTokenRef.current) return
        const st = await apApi.pollBillExtraction(extractionId)
        if (token !== removalTokenRef.current) return
        if (st.status === 'failed') {
          throw new Error(st.error || 'Extraction failed on the server.')
        }
        if (st.status === 'ready') {
          const f = st.extracted_fields || {}
          if (f.vendor_invoice_number) setVendorInvoiceNumber(f.vendor_invoice_number)
          if (f.issue_date) setIssueDate(f.issue_date)
          if (f.due_date) setDueDate(f.due_date)
          if (f.currency) setCurrency(f.currency.toUpperCase())
          if (f.notes) setNotes(f.notes)
          if (f.terms) setTerms(f.terms)
          // Phase 080 totals: header VAT rate + shipping + discount.
          // The extractor's reconcile step makes these consistent with
          // total = subtotal + tax + shipping - discount.
          if (f.discount != null) setDiscountInput(String(f.discount))
          if (f.shipping_amount != null) setShippingInput(String(f.shipping_amount))
          if (f.header_tax_rate_pct != null) {
            setHeaderTaxRatePctInput(String(f.header_tax_rate_pct))
            setUseHeaderTax(true)
          }
          if ((st.extracted_lines?.length ?? 0) > 0) {
            setLines(st.extracted_lines.map(l => ({
              description: l.description || '',
              account_code: l.suggested_account_code || '',
              quantity: l.quantity ?? 1,
              unit_price: l.unit_price ?? 0,
              tax_rate: l.tax_rate ?? 0,
            })))
          }
          setFieldConfidence(st.field_confidence || {})
          setOverallConfidence(st.overall_confidence)

          if (st.vendor_match) {
            setVendorId(st.vendor_match.id)
            toast({
              title: `Vendor matched: ${st.vendor_match.display_name}`,
              description: 'Fields auto-filled - review and save.',
            })
          } else if (f.vendor_name) {
            const draftCurrency = (f.currency || regional.currency).toUpperCase()
            // The model sometimes returns the country name ("United Kingdom")
            // or an ISO-3 code ("GBR") instead of the ISO-2 the picker needs.
            // Normalize first; back-fill from currency only if normalization
            // produced nothing (e.g. GBP → GB when the country wasn't seen).
            const draftCountry =
              normalizeCountryCode(f.vendor_country) ||
              countryForCurrency(draftCurrency) ||
              ''
            setVendorDraft({
              display_name: f.vendor_name,
              email: f.vendor_email || '',
              phone: f.vendor_phone || '',
              address_line1: f.vendor_address_line1 || '',
              address_line2: f.vendor_address_line2 || '',
              city: f.vendor_city || '',
              state_region: f.vendor_state_region || '',
              postal_code: f.vendor_postal_code || '',
              country: draftCountry,
              tax_id: f.vendor_tax_id || '',
              default_currency: draftCurrency,
              default_payment_terms_days: 30,
            })
            setBankDraft({
              label: 'Primary',
              is_primary: true,
              bank_name: f.vendor_bank_name || null,
              account_holder_name: f.vendor_bank_account_holder_name || f.vendor_name || null,
              account_number: f.vendor_bank_account_number || null,
              routing_number: f.vendor_bank_routing_number || null,
              iban: f.vendor_bank_iban || null,
              swift: f.vendor_bank_swift || null,
              country: draftCountry || null,
            })
            setVendorConfirmOpen(true)
          } else {
            toast({
              title: 'Extraction complete - vendor not detected',
              description: 'Pick a vendor manually below.',
            })
          }
          break
        }
        // else: 'pending' - keep polling.
      }
    } catch (err) {
      if (token !== removalTokenRef.current) return
      toast({ title: err instanceof Error ? err.message : 'Extraction failed', variant: 'destructive' })
    } finally {
      if (token === removalTokenRef.current) setExtracting(false)
    }
  }

  async function onConfirmVendor() {
    if (!vendorDraft.display_name?.trim()) {
      toast({ title: 'Vendor name is required', variant: 'destructive' }); return
    }
    try {
      const res = await createVendorMut.mutateAsync(vendorDraft)
      const newVendorId = res.vendor.id
      setVendorId(newVendorId)
      // If any bank field has content, create a primary bank account
      // for the new vendor. Silent failure is OK - the bill can still
      // be created without bank details.
      const hasBankInfo = !!(bankDraft.bank_name || bankDraft.account_number
        || bankDraft.routing_number || bankDraft.iban || bankDraft.swift)
      if (hasBankInfo) {
        try {
          await apApi.upsertBankAccount(newVendorId, bankDraft)
        } catch (err) {
          // Don't block the vendor-create flow on a bank write; just tell the user.
          toast({
            title: 'Vendor created - bank details failed',
            description: err instanceof Error ? err.message : 'Add bank account from the vendor page.',
            variant: 'destructive',
          })
          setVendorConfirmOpen(false)
          setBankDraft(blankBankDraft())
          return
        }
      }
      setVendorConfirmOpen(false)
      setBankDraft(blankBankDraft())
      toast({
        title: `Vendor "${res.vendor.display_name}" created`,
        description: hasBankInfo ? 'Primary bank account attached.' : 'Review the bill and save when ready.',
      })
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed to create vendor', variant: 'destructive' })
    }
  }

  // -------------------------------------------------------------------
  // Save bill - same logic for both modes.
  // -------------------------------------------------------------------
  async function onSave() {
    if (!vendorId) { toast({ title: 'Pick a vendor', variant: 'destructive' }); return }
    if (lines.length === 0 || !lines.some(l => l.description && l.account_code)) {
      toast({ title: 'At least one valid line item with a description + account code is required', variant: 'destructive' })
      return
    }
    const discountNum = Number(discountInput) || 0
    const shippingNum = Number(shippingInput) || 0
    const headerTaxRatePctNum =
      useHeaderTax && headerTaxRatePctInput.trim() !== '' ? Number(headerTaxRatePctInput) : null
    if (shippingNum > 0 && !shippingAccountCode) {
      toast({
        title: 'Pick a shipping expense account',
        description: 'Shipping must be assigned to an account before the bill can be posted.',
        variant: 'destructive',
      })
      return
    }
    const payload: BillPayload = {
      vendor_id: vendorId,
      vendor_invoice_number: vendorInvoiceNumber || undefined,
      issue_date: issueDate,
      due_date: dueDate,
      currency,
      notes: notes || undefined,
      terms: terms || undefined,
      attachment_s3_key: attachment?.key,
      discount: discountNum > 0 ? discountNum : undefined,
      shipping_amount: shippingNum > 0 ? shippingNum : undefined,
      shipping_account_code: shippingNum > 0 ? shippingAccountCode : null,
      header_tax_rate_pct:
        headerTaxRatePctNum != null && Number.isFinite(headerTaxRatePctNum)
          ? headerTaxRatePctNum
          : null,
      lines: lines.map(l => ({
        description: l.description,
        account_code: l.account_code,
        quantity: Number(l.quantity) || 0,
        unit_price: Number(l.unit_price) || 0,
        tax_rate:
          headerTaxRatePctNum != null && Number.isFinite(headerTaxRatePctNum)
            ? 0
            : Number(l.tax_rate) || 0,
      })),
    }
    await submitBill(payload)
  }

  // Centralised save: catches the 409 DUPLICATE_BILL response and opens
  // the duplicate-confirmation modal. Everything else bubbles up as a
  // toast. `force: true` skips the server-side duplicate guard.
  async function submitBill(payload: BillPayload) {
    try {
      const res = await createMut.mutateAsync(payload)
      toast({ title: 'Bill created' })
      navigate(`/invoicing/bills/${res.bill.id}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409
          && err.body && typeof err.body === 'object'
          && (err.body as { code?: string }).code === 'DUPLICATE_BILL') {
        const body = err.body as { duplicates?: DuplicateBillHit[] }
        setDuplicateHits(body.duplicates ?? [])
        setPendingPayload(payload)
        return
      }
      toast({ title: err instanceof Error ? err.message : 'Save failed', variant: 'destructive' })
    }
  }

  async function confirmForceSave() {
    if (!pendingPayload) return
    setForceSaving(true)
    try {
      await submitBill({ ...pendingPayload, force: true })
      setDuplicateHits(null)
      setPendingPayload(null)
    } finally {
      setForceSaving(false)
    }
  }

  function cancelDuplicatePrompt() {
    setDuplicateHits(null)
    setPendingPayload(null)
    // User bailed because this was a duplicate - clear the form all
    // the way down so they can start a brand-new bill without having
    // to hand-clear every field.
    removeAttachment()
  }

  // Confidence badge: shown next to extracted fields when the AI is
  // < 0.85 confident so the user double-checks.
  function ConfidenceBadge({ field }: { field: string }) {
    const c = fieldConfidence[field]
    if (c == null || c >= 0.85) return null
    return <Badge variant="warning" className="ml-1 text-[0.65rem]">~{Math.round(c * 100)}%</Badge>
  }

  useEffect(() => {
    if (initialVendorId) setMode('manual')
  }, [initialVendorId])

  // Dirty-check the bill editor. The baseline mirrors the empty-form
  // state set up in resetFormToDefaults() above, so the user has to
  // either pick a vendor, type a bill number, attach a file, or change
  // any other field before Save lights up.
  const formSnapshot = useMemo(
    () => ({
      vendorId,
      vendorInvoiceNumber,
      issueDate,
      dueDate,
      currency,
      notes,
      terms,
      discountInput,
      shippingInput,
      headerTaxRatePctInput,
      useHeaderTax,
      shippingAccountCode,
      attachmentKey: attachment?.key ?? null,
      lines,
    }),
    [
      vendorId, vendorInvoiceNumber, issueDate, dueDate, currency, notes, terms,
      discountInput, shippingInput, headerTaxRatePctInput, useHeaderTax, shippingAccountCode,
      attachment, lines,
    ],
  )
  const baselineSnapshot = useMemo(
    () => ({
      vendorId: initialVendorId || 0,
      vendorInvoiceNumber: '',
      issueDate: new Date().toISOString().slice(0, 10),
      // dueDate baseline matches the +30d initialiser; we compare against
      // the same shape so just opening the page isn't enough to flip dirty.
      dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
      currency: regional.currency,
      notes: '',
      terms: '',
      discountInput: '',
      shippingInput: '',
      headerTaxRatePctInput: '',
      useHeaderTax: false,
      shippingAccountCode: '',
      attachmentKey: null as string | null,
      lines: [blankLine()],
    }),
    [initialVendorId, regional.currency],
  )
  const dirty = useDirty(formSnapshot, baselineSnapshot)

  return (
    <Page
      title="New bill"
      actions={
        <Toolbar align="end">
          <Button variant="ghost" onClick={() => navigate('/invoicing/bills')}>
            <ArrowLeft className="mr-1 size-4" /> Back
          </Button>
          <Button onClick={onSave} disabled={!dirty || createMut.isPending || busy}>
            <Save className="mr-1 size-4" /> Save
          </Button>
        </Toolbar>
      }
    >
      {/* Mode tabs - both render as proper buttons so the user sees
          two affordances of equal visual weight. Active is primary
          (blue); inactive is secondary (gray bg). Locked while busy
          so switching modes mid-extraction can't strand state. */}
      <Card>
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Button
            variant={mode === 'manual' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('manual')}
            disabled={busy}
          >
            Enter manually
          </Button>
          <Button
            variant={mode === 'upload' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode('upload')}
            disabled={busy}
          >
            <Sparkles className="mr-1 size-4" /> Upload Bill + auto-fill
          </Button>
          <span className="ml-auto self-center text-xs text-muted-foreground">
            {mode === 'upload'
              ? 'Pick a vendor bill (PDF, PNG or JPG) - we extract header + line items + bank details with AI and pre-fill the form.'
              : 'Type fields by hand.'}
          </span>
        </div>
      </Card>

      {/* Attachment card - only rendered in upload mode. Manual mode
          stays clean; there's no "attach a PDF for reference" option
          any more. */}
      {mode === 'upload' ? (
        <Card>
          <div className="space-y-3 p-4">
            {attachment ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <FileText className="size-4 text-muted-foreground" />
                  <span className="font-medium">{attachment.filename}</span>
                  <span className="text-xs text-muted-foreground">({(attachment.size / 1024).toFixed(1)} KB)</span>
                  {extracting ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="size-3 animate-spin" /> Extracting…
                    </span>
                  ) : null}
                  {overallConfidence != null && !extracting ? (
                    <Badge variant={overallConfidence >= 0.85 ? 'success' : 'warning'} className="ml-2">
                      {Math.round(overallConfidence * 100)}% confidence
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {!extracting ? (
                    <Button size="sm" variant="ghost" onClick={() => runExtraction(attachment.key)}>
                      <Sparkles className="mr-1 size-4" /> Re-extract
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={removeAttachment}>
                    <XCircle className="mr-1 size-4 text-destructive" /> Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-md border-2 border-dashed p-6 text-center">
                <UploadCloud className="mx-auto mb-2 size-8 text-muted-foreground" />
                <p className="text-sm font-medium">
                  Drop a vendor bill here, or click to browse.
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  We&rsquo;ll process it with AI and pre-fill the form. PDF, PNG, JPG - up to 25 MB.
                </p>
                <Button variant="primary" className="mt-3" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <UploadCloud className="mr-1 size-4" /> {uploading ? 'Uploading…' : 'Choose file'}
                </Button>
                {/* eslint-disable-next-line no-restricted-syntax -- file input has no shadcn primitive */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  className="hidden"
                  onChange={onPickFile}
                />
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {/* Form - same shape regardless of mode; AI mode pre-fills it.
          While the attachment is uploading or extracting the whole card
          is locked out (pointer-events-none + opacity dim) so the user
          can't race the AI with manual edits and can't switch modes
          mid-flight. The only escape hatch is the Remove button on the
          attachment card above. */}
      <Card className={busy ? 'pointer-events-none opacity-60' : undefined}>
        <fieldset disabled={busy} className="space-y-4 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <FormField
              label={<>Vendor<ConfidenceBadge field="vendor_name" /></>}
              required
              control={
                <Combobox
                  value={vendorId || null}
                  onChange={v => setVendorId(v ? Number(v) : 0)}
                  options={(vendorsQ.data?.vendors ?? []).map(v => ({
                    value: v.id, label: v.display_name, description: v.email || undefined,
                  }))}
                  placeholder="Select a vendor…"
                  emptyMessage="No vendors yet."
                />
              }
            />
            <FormField
              label={<>Vendor invoice #<ConfidenceBadge field="vendor_invoice_number" /></>}
              value={vendorInvoiceNumber}
              onChange={e => setVendorInvoiceNumber(e.currentTarget.value)}
            />
            <FormField
              label={<>Issue date<ConfidenceBadge field="issue_date" /></>}
              type="date" value={issueDate} onChange={e => setIssueDate(e.currentTarget.value)}
            />
            <FormField
              label={<>Due date<ConfidenceBadge field="due_date" /></>}
              type="date" value={dueDate} onChange={e => setDueDate(e.currentTarget.value)}
            />
            <FormField
              label={<>Currency<ConfidenceBadge field="currency" /></>}
              control={
                <CurrencyCombobox
                  value={currency}
                  onChange={v => setCurrency(v)}
                />
              }
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold">Line items</div>
              <Button size="sm" variant="secondary" onClick={() => setLines([...lines, blankLine()])}>
                <Plus className="mr-1 size-4" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid gap-2 rounded-md border bg-card p-3 md:grid-cols-12">
                  <FormField
                    label="#"
                    containerClassName="md:col-span-1"
                    value={String(i + 1)}
                    disabled
                  />
                  <FormField
                    label="Description"
                    containerClassName="md:col-span-3"
                    value={l.description}
                    onChange={e => setLine(i, { description: e.currentTarget.value })}
                  />
                  <FormField
                    label="Expense account"
                    containerClassName="md:col-span-3"
                    control={
                      <Combobox
                        value={l.account_code || null}
                        onChange={v => setLine(i, { account_code: v ? String(v) : '' })}
                        options={expenseAccountOptions}
                        placeholder="Pick account…"
                        emptyMessage="No expense accounts in your CoA."
                      />
                    }
                  />
                  <FormField
                    label="Unit price"
                    type="number"
                    step="0.01"
                    containerClassName="md:col-span-1"
                    value={String(l.unit_price)}
                    onChange={e => setLine(i, { unit_price: Number(e.currentTarget.value) || 0 })}
                  />
                  <FormField
                    label="Qty"
                    type="number"
                    containerClassName="md:col-span-1"
                    value={String(l.quantity)}
                    onChange={e => setLine(i, { quantity: Number(e.currentTarget.value) || 0 })}
                  />
                  <FormField
                    label="Tax %"
                    type="number"
                    step="0.01"
                    containerClassName="md:col-span-1"
                    value={String(l.tax_rate)}
                    onChange={e => setLine(i, { tax_rate: Number(e.currentTarget.value) || 0 })}
                  />
                  <div className="md:col-span-1 flex items-end justify-end">
                    <div className="flex items-center">
                      <Button size="icon" variant="ghost" className="size-7"
                        onClick={() => moveLine(i, -1)} disabled={i === 0}
                        aria-label="Move line up" type="button">
                        <ArrowUp className="size-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="size-7"
                        onClick={() => moveLine(i, 1)} disabled={i === lines.length - 1}
                        aria-label="Move line down" type="button">
                        <ArrowDown className="size-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="size-7"
                        onClick={() => removeLine(i)} disabled={lines.length === 1}
                        aria-label="Remove line" type="button">
                        <Trash2 className="size-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(() => {
            const subtotal = lines.reduce(
              (s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
              0,
            )
            const headerRate =
              useHeaderTax && headerTaxRatePctInput.trim() !== '' ? Number(headerTaxRatePctInput) : null
            const discountVal = Math.max(0, Number(discountInput) || 0)
            const shippingVal = Math.max(0, Number(shippingInput) || 0)
            const taxFromHeader =
              headerRate != null && Number.isFinite(headerRate) && headerRate >= 0
                ? Math.max(0, subtotal - discountVal) * (headerRate / 100)
                : null
            const taxPerLine =
              headerRate != null && Number.isFinite(headerRate)
                ? 0
                : lines.reduce((s, l) => {
                    const lineGross = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0)
                    return s + lineGross * ((Number(l.tax_rate) || 0) / 100)
                  }, 0)
            const taxTotal = taxFromHeader != null ? taxFromHeader : taxPerLine
            const total = Math.max(0, subtotal + taxTotal + shippingVal - discountVal)
            const fmt = (n: number) => formatInvoiceMoney(n, currency)
            return (
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      id="bill-use-header-tax"
                      checked={useHeaderTax}
                      onCheckedChange={(v) => setUseHeaderTax(v === true)}
                    />
                    <label htmlFor="bill-use-header-tax" className="cursor-pointer">
                      Apply a single tax rate to the whole bill (overrides per-line Tax %)
                    </label>
                  </div>
                  {useHeaderTax ? (
                    <FormField
                      label="Tax rate %"
                      type="number"
                      step="0.01"
                      min={0}
                      value={headerTaxRatePctInput}
                      onChange={e => setHeaderTaxRatePctInput(e.currentTarget.value)}
                      hint="e.g. 20 for 20%"
                    />
                  ) : null}
                  <FormField
                    label="Discount"
                    type="number"
                    step="0.01"
                    min={0}
                    value={discountInput}
                    onChange={e => setDiscountInput(e.currentTarget.value)}
                    hint="Loyalty / promo / volume discount. Always positive."
                  />
                  <FormField
                    label="Shipping"
                    type="number"
                    step="0.01"
                    min={0}
                    value={shippingInput}
                    onChange={e => setShippingInput(e.currentTarget.value)}
                  />
                  {Number(shippingInput) > 0 ? (
                    <FormField
                      label="Shipping expense account"
                      control={
                        <Combobox<string>
                          value={shippingAccountCode || null}
                          onChange={v => setShippingAccountCode(v ? String(v) : '')}
                          options={expenseAccountOptions}
                          placeholder="Select expense account…"
                          emptyMessage="No expense accounts found in your CoA."
                        />
                      }
                      hint="Required when shipping > 0; debited when the bill posts to GL."
                    />
                  ) : null}
                </div>
                <div className="rounded-md border bg-muted/30 p-4 text-sm">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Totals</div>
                  <dl className="space-y-1.5">
                    <div className="flex justify-between"><dt>Subtotal</dt><dd>{fmt(subtotal)}</dd></div>
                    {discountVal > 0 ? (
                      <div className="flex justify-between text-muted-foreground"><dt>Discount</dt><dd>−{fmt(discountVal)}</dd></div>
                    ) : null}
                    {shippingVal > 0 ? (
                      <div className="flex justify-between"><dt>Shipping</dt><dd>{fmt(shippingVal)}</dd></div>
                    ) : null}
                    {taxTotal > 0 ? (
                      <div className="flex justify-between">
                        <dt>{headerRate != null ? `Tax (${headerRate}%)` : 'Tax'}</dt>
                        <dd>{fmt(taxTotal)}</dd>
                      </div>
                    ) : null}
                    <div className="flex justify-between border-t pt-1.5 text-base font-semibold"><dt>Total</dt><dd>{fmt(total)}</dd></div>
                  </dl>
                </div>
              </div>
            )
          })()}

          <div className="grid gap-3 md:grid-cols-2">
            <FormField
              label="Notes"
              control={<textarea className="min-h-[72px] w-full rounded-md border bg-card px-3 py-2 text-sm" value={notes} onChange={e => setNotes(e.currentTarget.value)} />}
            />
            <FormField
              label="Terms"
              control={<textarea className="min-h-[72px] w-full rounded-md border bg-card px-3 py-2 text-sm" value={terms} onChange={e => setTerms(e.currentTarget.value)} />}
            />
          </div>
        </fieldset>
      </Card>

      {/* Vendor-confirmation popup - opens after extraction when the
          extracted vendor doesn't match an existing one. Now also
          pre-fills a primary bank-account draft so the user creates
          the vendor + bank details in one flow. */}
      <Modal
        open={vendorConfirmOpen}
        onOpenChange={setVendorConfirmOpen}
        title="Create vendor from extracted details?"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We didn&apos;t find a vendor matching <strong>{vendorDraft.display_name}</strong>.
            Review the AI-suggested details and create the vendor (it&apos;ll be auto-selected on this bill).
            You can edit any field before saving.
          </p>

          <div>
            <div className="mb-2 text-sm font-semibold">Vendor</div>
            <div className="space-y-3">
              <FormField label="Display name" required value={vendorDraft.display_name} onChange={e => setVendorDraft({ ...vendorDraft, display_name: e.currentTarget.value })} />
              <div className="grid gap-3 md:grid-cols-2">
                <FormField label="Email" type="email" value={vendorDraft.email || ''} onChange={e => setVendorDraft({ ...vendorDraft, email: e.currentTarget.value })} />
                <FormField label="Phone" value={vendorDraft.phone || ''} onChange={e => setVendorDraft({ ...vendorDraft, phone: e.currentTarget.value })} />
                <FormField label="Tax ID" value={vendorDraft.tax_id || ''} onChange={e => setVendorDraft({ ...vendorDraft, tax_id: e.currentTarget.value })} />
                <FormField
                  label="Country"
                  control={
                    <CountryCombobox
                      value={vendorDraft.country || null}
                      onChange={v => setVendorDraft({ ...vendorDraft, country: v || '', state_region: '' })}
                      onCurrencyChange={c => {
                        // Only fill currency from the picked country when
                        // currency hasn't been set yet - otherwise keep the
                        // user's explicit pick (e.g. UK address with USD billing).
                        if (!vendorDraft.default_currency) {
                          setVendorDraft({ ...vendorDraft, default_currency: c })
                        }
                      }}
                    />
                  }
                />
                <FormField
                  label="Default currency"
                  control={
                    <CurrencyCombobox
                      value={vendorDraft.default_currency || regional.currency}
                      onChange={v => setVendorDraft({
                        ...vendorDraft,
                        default_currency: v,
                        country: vendorDraft.country || countryForCurrency(v) || '',
                      })}
                    />
                  }
                />
                <FormField label="Address line 1" value={vendorDraft.address_line1 || ''} onChange={e => setVendorDraft({ ...vendorDraft, address_line1: e.currentTarget.value })} />
                <FormField label="City" value={vendorDraft.city || ''} onChange={e => setVendorDraft({ ...vendorDraft, city: e.currentTarget.value })} />
                <FormField
                  label={
                    vendorDraft.country?.toUpperCase() === 'US'
                      ? 'State'
                      : vendorDraft.country?.toUpperCase() === 'CA'
                        ? 'Province'
                        : 'State / region'
                  }
                  control={
                    <StateProvinceField
                      country={vendorDraft.country}
                      value={vendorDraft.state_region || ''}
                      onChange={v => setVendorDraft({ ...vendorDraft, state_region: v })}
                    />
                  }
                />
                <FormField label="Postal code" value={vendorDraft.postal_code || ''} onChange={e => setVendorDraft({ ...vendorDraft, postal_code: e.currentTarget.value })} />
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold">Bank account (optional)</div>
            <p className="mb-2 text-xs text-muted-foreground">
              Pre-filled from the invoice&apos;s bank details. Leave blank to skip - you can add bank accounts later from the vendor page.
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              <FormField label="Label" value={bankDraft.label} onChange={e => setBankDraft({ ...bankDraft, label: e.currentTarget.value })} />
              <FormField label="Bank name" value={bankDraft.bank_name || ''} onChange={e => setBankDraft({ ...bankDraft, bank_name: e.currentTarget.value })} />
              <FormField label="Account holder" value={bankDraft.account_holder_name || ''} onChange={e => setBankDraft({ ...bankDraft, account_holder_name: e.currentTarget.value })} />
              <FormField label="Account number" value={bankDraft.account_number || ''} onChange={e => setBankDraft({ ...bankDraft, account_number: e.currentTarget.value })} hint="Stored server-side, only last-4 is displayed back" />
              <FormField label="Routing number" value={bankDraft.routing_number || ''} onChange={e => setBankDraft({ ...bankDraft, routing_number: e.currentTarget.value })} />
              <FormField label="IBAN" value={bankDraft.iban || ''} onChange={e => setBankDraft({ ...bankDraft, iban: e.currentTarget.value })} />
              <FormField label="SWIFT / BIC" value={bankDraft.swift || ''} onChange={e => setBankDraft({ ...bankDraft, swift: e.currentTarget.value })} />
              <FormField
                label="Country"
                control={<CountryCombobox value={bankDraft.country || null} onChange={v => setBankDraft({ ...bankDraft, country: v || null })} />}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setVendorConfirmOpen(false); setBankDraft(blankBankDraft()) }}>Skip - pick existing</Button>
            <Button onClick={onConfirmVendor} disabled={createVendorMut.isPending}>
              <Plus className="mr-1 size-4" /> Create vendor
            </Button>
          </div>
        </div>
      </Modal>

      {/* Duplicate-bill prompt - the backend returns 409 when the
          payload matches an existing non-voided bill for the same
          vendor (by vendor_invoice_number, or by amount + date). The
          user can Cancel (leave draft as-is) or Create anyway (re-post
          with force=true). */}
      <Modal
        open={duplicateHits !== null}
        onOpenChange={(o) => { if (!o) cancelDuplicatePrompt() }}
        title="Possible duplicate bill"
        description="A similar bill already exists for this vendor. Review the matches below before creating."
        footer={
          <>
            <Button variant="secondary" onClick={cancelDuplicatePrompt} disabled={forceSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmForceSave} disabled={forceSaving}>
              {forceSaving ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
              Create anyway
            </Button>
          </>
        }
      >
        <ul className="space-y-2">
          {(duplicateHits ?? []).map((h) => (
            <li key={h.id} className="rounded-md border p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{h.bill_number}</span>
                <Badge variant="secondary">{h.status}</Badge>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {h.vendor_invoice_number ? <>Vendor inv: <span className="font-mono">{h.vendor_invoice_number}</span> · </> : null}
                Issue {String(h.issue_date).slice(0, 10)} · Due {String(h.due_date).slice(0, 10)}
                {' · '}Total {formatInvoiceMoney(h.total, h.currency)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Created {formatDateTime(h.created_at)}
              </div>
              {h.match_reasons?.length ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {h.match_reasons.includes('same_vendor_invoice_number') ? (
                    <Badge variant="warning">same vendor invoice #</Badge>
                  ) : null}
                  {h.match_reasons.includes('same_issue_date_and_total') ? (
                    <Badge variant="warning">same issue date + total</Badge>
                  ) : null}
                  {h.match_reasons.includes('same_due_date_and_total') ? (
                    <Badge variant="warning">same due date + total</Badge>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </Modal>
    </Page>
  )
}
